import { Link } from "react-router-dom";
import { BookOpen, Users, Search, Target, ArrowRight, Globe, Compass, Library } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";
import { useAuth } from "@/hooks/useAuth";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const Index = () => {
  const { user } = useAuth();
  const { canCreateContent, hasRole } = useRoleAccess();

  // Two main pillars as per the vision
  const knowledgePillars = [
    {
      title: "Human Inquiry",
      subtitle: "The Inward Journey",
      description: "Explore the moral, rational, and interpretive aspects of knowledge",
      disciplines: ["Theology", "Philosophy", "History", "Law"],
      icon: Users,
      path: "/human-inquiry",
      color: "border-blue-200 hover:border-blue-300"
    },
    {
      title: "The World Itself", 
      subtitle: "The Outward Discovery",
      description: "Understand the physical, material, and empirical aspects of reality",
      disciplines: ["Mathematics", "Natural Sciences", "Engineering", "Health Sciences"],
      icon: Globe,
      path: "/world-itself",
      color: "border-green-200 hover:border-green-300"
    }
  ];

  // Quick navigation for immediate access
  const quickAccess = [
    {
      title: "Explore Themes",
      description: "Cross-disciplinary concepts like Justice, Infinity, Consciousness",
      icon: Compass,
      path: "/themes"
    },
    {
      title: "Library",
      description: "Articles, lectures, foundational texts, and case studies",
      icon: Library,
      path: "/library"
    },
    {
      title: "Search Knowledge",
      description: "Find specific topics across all fields of inquiry", 
      icon: Search,
      path: "/search"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Cathedral Entrance */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <div className="mb-8">
            <h1 className="text-5xl font-light text-black mb-4 tracking-wide">
              The Academy
            </h1>
            <p className="text-xl text-gray-700 mb-2 font-light">
              A Cathedral of Knowledge
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Where each field is a column holding up a greater whole. Discover the unity of knowledge 
              through structured inquiry across disciplines.
            </p>
          </div>

          {/* Quick Entry for Non-Students */}
          {!user && (
            <div className="flex items-center justify-center gap-3 mb-16">
              <Button variant="outline" size="lg" asChild className="bg-white border-gray-300">
                <Link to="/auth/login">Sign In</Link>
              </Button>
              <Button size="lg" asChild className="bg-black text-white hover:bg-gray-800">
                <Link to="/auth/signup">Join as Learner</Link>
              </Button>
            </div>
          )}

          {/* Logged-in User Quick Access */}
          {user && (
            <div className="flex items-center justify-center gap-3 mb-16">
              <Button size="lg" asChild className="bg-black text-white hover:bg-gray-800">
                <Link to="/dashboard">My Dashboard</Link>
              </Button>
              {canCreateContent() && (
                <Button variant="outline" size="lg" asChild>
                  <Link to="/content">Content Hub</Link>
                </Button>
              )}
              {hasRole('admin') && (
                <Button variant="outline" size="lg" asChild>
                  <Link to="/admin">Admin Portal</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Two Pillars - Core Architecture */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-black mb-4">Pillars of Inquiry</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Two interwoven pillars that converge in thematic bridges, echoing the same questions through different media.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {knowledgePillars.map((pillar, index) => (
              <Card key={index} className={`border-2 ${pillar.color} transition-all duration-300 hover:shadow-lg`}>
                <CardHeader className="text-center pb-6">
                  <div className="mx-auto w-16 h-16 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center mb-4">
                    <pillar.icon className="w-8 h-8 text-gray-700" />
                  </div>
                  <CardTitle className="text-2xl text-black font-light">{pillar.title}</CardTitle>
                  <p className="text-sm text-gray-600 font-medium">{pillar.subtitle}</p>
                  <p className="text-gray-600 mt-2">{pillar.description}</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {pillar.disciplines.map((discipline, idx) => (
                      <div key={idx} className="text-center p-2 bg-white rounded border text-sm text-gray-700">
                        {discipline}
                      </div>
                    ))}
                  </div>
                  <Button asChild className="w-full bg-black text-white hover:bg-gray-800">
                    <Link to={pillar.path}>
                      Enter <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Navigation - Clean Access */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-light text-black mb-4">Navigate Knowledge</h3>
            <p className="text-gray-600">Direct paths to understanding</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickAccess.map((item, index) => (
              <Card key={index} className="border border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-md">
                <CardContent className="p-8 text-center">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h4 className="font-medium text-black mb-3 text-lg">{item.title}</h4>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">{item.description}</p>
                  <Button asChild variant="outline" className="w-full border-gray-300 hover:bg-gray-50">
                    <Link to={item.path}>Access</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Convergence - Interdisciplinary Bridge */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="mb-8">
            <Target className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <h3 className="text-2xl font-light text-black mb-4">Convergence</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Where disciplines meet and wisdom emerges. Explore the interconnected nature of all knowledge.
            </p>
          </div>
          <Button asChild size="lg" className="bg-black text-white hover:bg-gray-800">
            <Link to="/convergence">
              Explore Intersections <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-12 px-4 border-t border-gray-200">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-gray-600 text-sm">
            A structured approach to human understanding • {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
